import { useEffect, useState } from "react";
import { post } from "@common/api/apiShared.ts";
import { useTranslation } from "@localization/useTranslation.ts";
import { SystemTemplateTypesEnum } from "../systemEdit/SystemEdit.types";
import { useValidator } from "@common/hooks/validation";
import { IAddSystemDialogProps } from "./AddSystemDialog";
import { GetSystemsPlanLimit } from "../../auth/planProvider/PlanProvider.hooks.ts";
import { usePlanContext } from "../../auth/planProvider/PlanProvider";
import { useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";
import { useDismissable } from "@common/hooks/useDismissable";
import { useSelectableEntityTemplatesForCreation } from "@common/hooks/useSelectableItemQueries.ts";
import { EntityTemplateField, getStandardFieldTranslationKey, SystemStandardField, TemplateEntityType } from "../../entityTemplates/EntityTemplate.types.ts";
import { toEntityTemplateField } from "../../entityTemplates/requiredAtCreation.ts";
import { buildSystemDefaults, fieldsTheUserMayEdit } from "../../entityTemplates/defaultValue/templateDefaults.ts";
import {
    CreateSystemSaveModel,
    createSystemSaveModel,
    getSystemFieldValue,
    mapEditedSystemToSaveFields,
    SystemCreationFieldSelections,
    systemCreationFieldIds,
    toCreateSystemRequest,
} from "../systemCreation.ts";
import { hasFieldData } from "../../entityTemplates/fieldValue.ts";
import { SystemModel } from "../systemEdit/SystemEdit.types.ts";
import { ValidationError } from "@common/validationError.ts";
import { DotLegalSelectOption } from "@common/components/dotLegalMultiSelect/DotLegalMultiSelect.types.ts";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider.tsx";

export function useAddSystemDialogDataMapping(props: IAddSystemDialogProps) {
    const { translateString } = useTranslation();
    const { refetch: refetchSystemsPlanLimitData } = GetSystemsPlanLimit();
    const { informationSecurityFeatures, platformFeatures } = usePlanContext();
    const { permissions } = useUserContext();

    const { visible: showInfoSecUpgradeAlert, onDismiss: onDismissInfoSecAlert } = useDismissable(
        "info-sec-upgrade-alert-dismissed",
        30,
        !informationSecurityFeatures.hasInfoSec
    );

    const { data: entityTemplatesData, isLoading: templatesIsLoading } = useSelectableEntityTemplatesForCreation(TemplateEntityType.System, false);

    const templates: Array<DotLegalSelectOption> | undefined = entityTemplatesData?.map((template) => ({
        id: template.id,
        name: template.name,
    }));

    const [systemModel, setSystemModel] = useState<CreateSystemSaveModel>(createSystemSaveModel(props.searchedName ?? ""));
    // Empty until the user changes something, so the template's default values show through until then
    const [systemEdits, setSystemEdits] = useState<Partial<SystemModel>>({});
    const [editedTagIds, setEditedTagIds] = useState<Array<string> | undefined>(undefined);
    const [creationFieldEdits, setCreationFieldEdits] = useState<SystemCreationFieldSelections>({});
    const [showRequiredFieldErrors, setShowRequiredFieldErrors] = useState(false);

    const selectedTemplate = entityTemplatesData?.find((t) => t.id === systemModel.entityTemplateId);
    const selectedTemplateMissingAccess = selectedTemplate?.missingRequiredAtCreationFieldAccess ?? false;

    const skippedFields: Set<number> = new Set([SystemStandardField.SystemName, SystemStandardField.SystemStatus]);

    const requiredAtCreationFields = (selectedTemplate?.requiredAtCreationFields ?? []).filter(
        (f) => !f.standardFieldIdentifier || !skippedFields.has(f.standardFieldIdentifier)
    );

    const requiredAtCreationTemplateFields = requiredAtCreationFields.map(toEntityTemplateField);

    // The dialog only knows the fields required at creation, so the backend fills in the defaults of the rest
    const templateDefaults = buildSystemDefaults(fieldsTheUserMayEdit(requiredAtCreationTemplateFields, platformFeatures.tags && permissions.canAccessTags));
    const editedSystem: Partial<SystemModel> = { ...templateDefaults.system, ...systemEdits };
    const selectedTagIds = editedTagIds ?? templateDefaults.tagIds;
    const creationFieldSelections: SystemCreationFieldSelections = { ...templateDefaults.creationFieldSelections, ...creationFieldEdits };
    const selectedSupplierIds = creationFieldSelections[SystemStandardField.SystemSuppliers] ?? [];
    const selectedAssetIds = creationFieldSelections[SystemStandardField.SystemAssociatedAssets] ?? [];
    const selectedDataSubjectIds = creationFieldSelections[SystemStandardField.SystemDataSubjects] ?? [];
    const selectedDataCategoryIds = creationFieldSelections[SystemStandardField.SystemDataCategories] ?? [];
    const selectedSecurityMeasureIds = creationFieldSelections[SystemStandardField.SystemSecurityMeasures] ?? [];
    const selectedPolicyIds = creationFieldSelections[SystemStandardField.SystemPolicies] ?? [];
    const changeCreationField = (field: SystemStandardField) => (selectedIds: Array<string>) =>
        setCreationFieldEdits((previous) => ({ ...previous, [field]: selectedIds }));

    const tagsRequiredAtCreation =
        platformFeatures.tags &&
        permissions.canAccessTags &&
        requiredAtCreationFields.some((f) => f.standardFieldIdentifier === SystemStandardField.SystemTags);

    const standardRequiredFields = requiredAtCreationTemplateFields
        .filter(
            (f) =>
                f.standardFieldIdentifier &&
                f.standardFieldIdentifier !== SystemStandardField.SystemTags &&
                !systemCreationFieldIds.has(f.standardFieldIdentifier)
        )
        .sort((a, b) => a.order - b.order);

    const suppliersRequiredAtCreation = requiredAtCreationFields.some((f) => f.standardFieldIdentifier === SystemStandardField.SystemSuppliers);
    const associatedAssetsRequiredAtCreation = requiredAtCreationFields.some((f) => f.standardFieldIdentifier === SystemStandardField.SystemAssociatedAssets);
    const dataSubjectsRequiredAtCreation = requiredAtCreationFields.some((f) => f.standardFieldIdentifier === SystemStandardField.SystemDataSubjects);
    const dataCategoriesRequiredAtCreation = requiredAtCreationFields.some((f) => f.standardFieldIdentifier === SystemStandardField.SystemDataCategories);
    const securityMeasuresRequiredAtCreation = requiredAtCreationFields.some((f) => f.standardFieldIdentifier === SystemStandardField.SystemSecurityMeasures);
    const policiesRequiredAtCreation = requiredAtCreationFields.some((f) => f.standardFieldIdentifier === SystemStandardField.SystemPolicies);

    const customRequiredFields = requiredAtCreationTemplateFields.filter((f) => f.customInputFieldId).sort((a, b) => a.order - b.order);

    const hasStandardRequiredFields =
        standardRequiredFields.length > 0 ||
        tagsRequiredAtCreation ||
        suppliersRequiredAtCreation ||
        associatedAssetsRequiredAtCreation ||
        dataSubjectsRequiredAtCreation ||
        dataCategoriesRequiredAtCreation ||
        securityMeasuresRequiredAtCreation ||
        policiesRequiredAtCreation;

    const hasRequiredFields = hasStandardRequiredFields || customRequiredFields.length > 0;

    const validation = useValidator<CreateSystemSaveModel>((validators) => ({
        name: validators.validateNotEmpty((item) => item.name, translateString("name")),
        entityTemplateId: validators.validateNotEmpty((item) => item.entityTemplateId, translateString("assetTemplate")),
    }));

    const validateRequiredAtCreationFields = (): Array<ValidationError> => {
        const errors: Array<ValidationError> = [];
        const requiredTranslation = translateString("requiredField");

        for (const field of requiredAtCreationTemplateFields) {
            if (field.standardFieldIdentifier === SystemStandardField.SystemTags) {
                if (selectedTagIds.length === 0) {
                    errors.push(new ValidationError(getStandardFieldTranslationKey(SystemStandardField.SystemTags), requiredTranslation));
                }
                continue;
            }

            if (field.standardFieldIdentifier && systemCreationFieldIds.has(field.standardFieldIdentifier)) {
                const selectedIds = creationFieldSelections[field.standardFieldIdentifier as SystemStandardField] ?? [];
                if (selectedIds.length === 0) {
                    errors.push(new ValidationError(getStandardFieldTranslationKey(field.standardFieldIdentifier), requiredTranslation));
                }
                continue;
            }

            const fieldName = field.standardFieldIdentifier ? SystemStandardField[field.standardFieldIdentifier].toString() : field.id;
            const value = getSystemFieldValue(field, editedSystem);
            if (!hasFieldData(value)) {
                errors.push(new ValidationError(fieldName, requiredTranslation));
            }
        }

        return errors;
    };

    const requiredFieldValidationErrors = showRequiredFieldErrors ? validateRequiredAtCreationFields() : [];

    const saveMutation = useDotLegalMutation(save, {
        onSuccess: (response: any) => {
            const id = response.value().id;

            refetchSystemsPlanLimitData();
            props.onSystemCreated(id);
        },
    });

    let templateTypes: Array<DotLegalSelectOption> = Object.entries(SystemTemplateTypesEnum)
        .filter((q) => isNaN(Number(q[0])))
        .map((q) => {
            return {
                name: translateString(q[0].toLowerCase()),
                id: q[1].toString(),
            };
        });

    const createSystem = async () => {
        if (validation.anyHasErrors) {
            validation.setShowErrors(true);
        }
        const racErrors = validateRequiredAtCreationFields();
        if (racErrors.length > 0) {
            setShowRequiredFieldErrors(true);
        }
        if (validation.anyHasErrors || racErrors.length > 0) return;

        await saveMutation.mutateAsync({
            ...systemModel,
            ...mapEditedSystemToSaveFields(editedSystem, selectedTagIds),
            answeredFieldIds: requiredAtCreationFields.map((field) => field.entityTemplateFieldId),
            supplierLegalEntityIds: selectedSupplierIds,
            associatedAssetIds: selectedAssetIds,
            dataSubjectIds: selectedDataSubjectIds,
            dataCategoryIds: selectedDataCategoryIds,
            securityMeasureIds: selectedSecurityMeasureIds,
            policyIds: selectedPolicyIds,
        });
    };

    function save(model: CreateSystemSaveModel) {
        return post<{}>("/System/", toCreateSystemRequest(model));
    }

    const onTemplateChange = (entityTemplateId: string | null) => {
        if (entityTemplateId) {
            setSystemModel((prev) => ({ ...prev, entityTemplateId }));
            setSystemEdits({});
            setEditedTagIds(undefined);
            setCreationFieldEdits({});
            setShowRequiredFieldErrors(false);
        }
    };

    useEffect(() => {
        if (entityTemplatesData?.length === 1 && !systemModel.entityTemplateId) {
            onTemplateChange(entityTemplatesData[0].id);
        }
    }, [entityTemplatesData, systemModel.entityTemplateId]);

    const handleRequiredFieldChange = async (changeFunc: (system: SystemModel) => void) => {
        const updated = { ...editedSystem } as SystemModel;
        changeFunc(updated);
        setSystemEdits(updated);
    };

    return {
        inProgress: saveMutation.isLoading,
        createSystem,
        systemModel,
        setSystemModel,
        validation,
        templateTypes,
        templates,
        templatesIsLoading,
        showInfoSecUpgradeAlert,
        onDismissInfoSecAlert,
        selectedTemplateMissingAccess,
        hasRequiredFields,
        hasStandardRequiredFields,
        standardRequiredFields,
        customRequiredFields,
        tagsRequiredAtCreation,
        selectedTagIds,
        setSelectedTagIds: setEditedTagIds,
        requiredFieldValidationErrors,
        editedSystem,
        handleRequiredStandardFieldChange: handleRequiredFieldChange,
        handleRequiredCustomFieldChange: handleRequiredFieldChange,
        onTemplateChange,
        permissions,
        suppliersRequiredAtCreation,
        associatedAssetsRequiredAtCreation,
        dataSubjectsRequiredAtCreation,
        dataCategoriesRequiredAtCreation,
        selectedSupplierIds,
        setSelectedSupplierIds: changeCreationField(SystemStandardField.SystemSuppliers),
        selectedAssetIds,
        setSelectedAssetIds: changeCreationField(SystemStandardField.SystemAssociatedAssets),
        selectedDataSubjectIds,
        setSelectedDataSubjectIds: changeCreationField(SystemStandardField.SystemDataSubjects),
        selectedDataCategoryIds,
        setSelectedDataCategoryIds: changeCreationField(SystemStandardField.SystemDataCategories),
        securityMeasuresRequiredAtCreation,
        selectedSecurityMeasureIds,
        setSelectedSecurityMeasureIds: changeCreationField(SystemStandardField.SystemSecurityMeasures),
        policiesRequiredAtCreation,
        selectedPolicyIds,
        setSelectedPolicyIds: changeCreationField(SystemStandardField.SystemPolicies),
    };
}
